import { AgencyVerificationStatus } from "@/enums/agencies";
import { Org, OrgAdmin } from "@/types/org";

type OrgLike = Org | OrgAdmin;

const WEBSITE_FIELD_KEY = "website";

/**
 * Common-namespace keys for org fields VerifyCustomerProfile requires,
 * matching the pre-check Settings → VerifyPhone shows.
 */
export function getMissingTwilioProfileFieldKeys(org: OrgLike): string[] {
  const missing: string[] = [];

  if (!org.owner?.phoneNumber?.length) {
    missing.push("phone");
  }
  if (!org.owner?.email?.length) {
    missing.push("email");
  }
  if (!org.name?.length) {
    missing.push("organization");
  }
  if (!org.address?.addressLine1?.length) {
    missing.push("address");
  }
  if (!org.address?.city?.length) {
    missing.push("city");
  }
  if (!org.address?.state?.length) {
    missing.push("state");
  }
  if (!org.address?.zipCode?.length) {
    missing.push("zip");
  }
  if (!org.owner?.firstName?.length) {
    missing.push("firstname");
  }
  if (!org.owner?.lastName?.length) {
    missing.push("lastname");
  }
  if (!org.details?.website?.length) {
    missing.push("website");
  }

  return missing;
}

export function hasGailTwilioNumber(org: OrgLike): boolean {
  return Boolean(org.services?.twilioPhoneNumber);
}

function isVerificationAlreadyRunning(org: OrgLike): boolean {
  const status = org.details?.agencyVerificationStatus;
  return (
    status === AgencyVerificationStatus.Pending ||
    status === AgencyVerificationStatus.Verified ||
    org.details?.legalNameCheck?.status === "started"
  );
}

/**
 * Last-step onboarding should only offer Trust Hub when it can actually start,
 * or when the remaining issue is the website URL (user can edit it and retry).
 * Missing owner/address/EIN/Gail number cannot be fixed on that page.
 */
export function shouldOfferPhoneVerificationStep(org: OrgLike | null | undefined): boolean {
  if (!org) {
    return false;
  }

  if (isVerificationAlreadyRunning(org)) {
    return true;
  }

  const checkStatus = org.details?.legalNameCheck?.status;
  if (checkStatus === "skipped" || checkStatus === "start_failed") {
    return false;
  }

  const blockingFields = getMissingTwilioProfileFieldKeys(org).filter(
    (key) => key !== WEBSITE_FIELD_KEY
  );
  if (blockingFields.length > 0) {
    return false;
  }

  if (!org.details?.businessId || !org.details?.twilioBusinessType) {
    return false;
  }

  return hasGailTwilioNumber(org);
}
